import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ChapterMarker from "@/components/ChapterMarker";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border-0 border-b border-[rgba(212,175,55,0.3)] bg-transparent py-3 font-lora text-sm text-abbey-cream placeholder:text-abbey-cream/35 focus:border-abbey-gold focus:outline-none transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message has been sent. Thank you for writing.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden bg-abbey-dark px-6 py-24 md:px-12 md:py-32 lg:px-24 lg:py-40"
    >
      <div className="pointer-events-none absolute -bottom-40 left-1/2 h-[60vw] w-[60vw] -translate-x-1/2 glow-orb" />

      <div className="relative mx-auto max-w-3xl">
        <ChapterMarker number="04" eyebrow="Chapter Four — Correspondence" title="Contact Us" dark />

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="-mt-6 mb-16 max-w-xl text-sm leading-relaxed text-abbey-cream/70 md:text-base"
        >
          Questions about the books, bulk orders for parishes or book clubs,
          speaking invitations, or simply a kind word — letters are always
          welcome.
        </motion.p>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: EASE, delay: 0.1 }}
          className="space-y-12"
        >
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="font-cinzel text-[10px] tracking-[0.3em] text-abbey-gold">
                YOUR NAME
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                type="text"
                required
                maxLength={120}
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Doe"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="font-cinzel text-[10px] tracking-[0.3em] text-abbey-gold">
                YOUR EMAIL
              </label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="jane@example.com"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact-message" className="font-cinzel text-[10px] tracking-[0.3em] text-abbey-gold">
              YOUR MESSAGE
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              required
              maxLength={4000}
              rows={4}
              value={form.message}
              onChange={update("message")}
              placeholder="Dear Fr. Stopa..."
              className={`${inputClass} resize-none`}
            />
          </div>

          <button
            data-testid="contact-submit-btn"
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-3 border border-abbey-gold bg-abbey-gold px-10 py-4 font-cinzel text-[11px] font-semibold tracking-[0.3em] text-abbey-dark transition-colors duration-300 hover:bg-transparent hover:text-abbey-gold disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <Send className="h-4 w-4" strokeWidth={1.5} />
            )}
            {sending ? "SENDING..." : "SEND MESSAGE"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
